import { getEspaceDistributeurServicesApi } from '@/core/lib/ApiUtils';
import Cache from '@/core/lib/Cache';
import { type MarcheCode, type MarqueClientCode, type OffreRubriqueCode, offresRubriques, type ReseauDistributionCode } from '@/core/lib/OffreUtils';

export interface OffreDetails {
  description: string;
  conditions: string[];
  pointsForts: string[];
  cibles: string[];
  offresAssociees: string[];
  rubriqueCodeList: OffreRubriqueCode[];
  offreDuMoment?: boolean;
}

export interface Tarificateur {
  uri: string;
  label: string;
  secondaire: boolean;
}

export interface Offre {
  code: string;
  label: string;
  reseauCodeList: ReseauDistributionCode[];
  marcheCodeList: MarcheCode[];
  marqueClient: MarqueClientCode;
  tarificateur?: Tarificateur;
  details: OffreDetails;
  documentationUri: string;
}

interface OffreStrapiDetailsApi {
  bouton_acces_oav: {
    libelle: string;
    secondaire: boolean;
  } | null;
  cibles: string[];
  conditions: string[];
  description: string;
  offres_complementaires: string[];
  points_forts: string[];
  offre_du_moment?: boolean;
}

export interface OffreApi {
  code: string;
  acces_oav?: {
    uri: string;
    scope_associee: string;
  };
  date_debut: string;
  date_fin: string;
  details?: OffreStrapiDetailsApi;
  documentation_uri: string;
  libelle: string;
  marches: MarcheCode[];
  marque_client_par_defaut: MarqueClientCode;
  rubriques: OffreRubriqueCode[];
  reseaux: ReseauDistributionCode[];
}

function mapTarificateur({ acces_oav, details }: OffreApi): Tarificateur | undefined {
  if (acces_oav?.uri) {
    return {
      uri: acces_oav.uri,
      label: details?.bouton_acces_oav?.libelle ?? "Tarifer l'offre",
      secondaire: details?.bouton_acces_oav?.secondaire ?? false,
    };
  }

  return undefined;
}

function mapDetails({ rubriques, details }: OffreApi): OffreDetails {
  return {
    description: details?.description ?? '',
    conditions: details?.conditions ?? [],
    pointsForts: details?.points_forts ?? [],
    cibles: details?.cibles ?? [],
    offresAssociees: details?.offres_complementaires ?? [],
    rubriqueCodeList: rubriques,
    offreDuMoment: details?.offre_du_moment,
  };
}

function mapOffres(offresApi: OffreApi[]): Offre[] {
  return offresApi.map(
    (offre: OffreApi): Offre => ({
      code: offre.code,
      label: offre.libelle,
      reseauCodeList: offre.reseaux,
      marcheCodeList: offre.marches,
      marqueClient: offre.marque_client_par_defaut,
      documentationUri: offre.documentation_uri,
      tarificateur: mapTarificateur(offre),
      details: mapDetails(offre),
    }),
  );
}

const cacheOffres = new Cache<Promise<Offre[]>>();

function getOffres(): Promise<Offre[]> {
  return cacheOffres.getOrSet('offres', () =>
    getEspaceDistributeurServicesApi()
      .then(axiosInstance => axiosInstance.get<OffreApi[]>('/offres'))
      .then(({ data }) => mapOffres(data)),
  );
}

type AllOffresByRubrique = Partial<Record<OffreRubriqueCode, Offre[]>>;

function toAllOffresByRubrique(offers: Offre[]): AllOffresByRubrique {
  return offers.reduce<AllOffresByRubrique>(
    (acc, offer) => {
      for (const rubrique of offer.details.rubriqueCodeList) {
        acc[rubrique]?.push(offer);
      }

      return acc;
    },
    offresRubriques.reduce<AllOffresByRubrique>((acc, rubrique) => {
      acc[rubrique.code] = [];

      return acc;
    }, {}),
  );
}

const cacheAllOffresByRubrique = new Cache<Promise<AllOffresByRubrique>>();

function getAllOffresByRubrique(): Promise<AllOffresByRubrique> {
  return cacheAllOffresByRubrique.getOrSet('offres-by-rubrique-hashmap', () => getOffres().then(toAllOffresByRubrique));
}

export async function getRubriqueOffres(rubriqueCode: OffreRubriqueCode): Promise<Offre[] | undefined> {
  const hashMap = await getAllOffresByRubrique();

  return hashMap[rubriqueCode];
}

const cacheComparateurEmprunteurUrl = new Cache<Promise<string>>();

export async function getComparateurEmprunteurUrl(): Promise<string> {
  return cacheComparateurEmprunteurUrl.getOrSet('comparateur-emprunteur-url', () =>
    getEspaceDistributeurServicesApi()
      .then(axiosInstance => axiosInstance.get<string>('/utilisateurs/connexion-comparateur-emprunteurs'))
      .then(({ data }) => data),
  );
}
